import React, { useEffect, useState, useRef } from 'react';
import { Edit, Trash2, Plus, Shield, LogOut, Search, X, TrendingUp, ChevronDown, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import LogoLoader from '../components/LogoLoader';
import toast from 'react-hot-toast';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const getLocalDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const s = {
  page: { height: '100vh', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  navbar: {
    background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)',
    padding: '0 20px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 20px rgba(0,0,0,0.3)',
    flexShrink: 0,
  },
  navBrand: { display: 'flex', alignItems: 'center', gap: '12px' },
  navCoin: {
    width: '42px', height: '42px',
    borderRadius: '50%', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  navTitle: { color: '#fff', fontSize: '20px', fontWeight: '800', margin: 0 },
  navSub: { color: '#94a3b8', fontSize: '12px', margin: 0 },
  navRight: { display: 'flex', gap: '10px', alignItems: 'center' },
  btnAdmin: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.1)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.2)',
    padding: '8px 16px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  btnLogout: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)',
    padding: '8px 16px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  content: {  padding: '32px 24px' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' },
  statCard: {
    background: '#fff', borderRadius: '14px',
    padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)',
    borderLeft: '4px solid #C9A84C',
  },
  statLabel: { fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statValue: { fontSize: '28px', fontWeight: '800', color: '#0f172a', margin: '4px 0 0' },
  statDate: { fontSize: '11px', color: '#94a3b8', margin: '4px 0 0' },
  tableCard: { background: '#fff', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', overflow: 'hidden' },
  tableHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
  },
  tableTitle: { fontSize: '17px', fontWeight: '700', color: '#0f172a', margin: 0 },
  btnAdd: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    color: '#fff', border: 'none', padding: '10px 18px',
    borderRadius: '9px', fontSize: '14px', fontWeight: '700',
    cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,168,76,0.3)',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', letterSpacing: '0.5px', position: 'sticky', top: 0, zIndex: 1 },
  td: { padding: '8px 20px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#334155' },
  badge: {
    display: 'inline-block', background: '#fef3c7', color: '#D97706',
    borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700',
  },
  actions: { display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '6px' },
  inputStyle: {
    width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0',
    borderRadius: '9px', fontSize: '14px', background: '#f8fafc',
    boxSizing: 'border-box', outline: 'none', marginBottom: '14px',
  },
  saveBtn: {
    width: '100%', padding: '12px', background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
    color: '#fff', border: 'none', borderRadius: '9px', fontSize: '15px',
    fontWeight: '700', cursor: 'pointer',
  },
};

export default function Dashboard() {
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [phoneVal, setPhoneVal] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchId, setSearchId] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const isDeletingRef = useRef(false);
  const [todayRate, setTodayRate] = useState(null);
  const [showRatePopup, setShowRatePopup] = useState(false);
  const [rateLoading, setRateLoading] = useState(false);
  const [updateRatePopup, setUpdateRatePopup] = useState(null); // 'gold' | 'silver' | null
  const [updateRateLoading, setUpdateRateLoading] = useState(false);
  const navigate = useNavigate();
  const { adminName } = useAuth();
  const ALLOWED_ADMIN_EMAIL = 'alameengoldandsilver@gmail.com';

  const handleExportXLS = async () => {
    const { data, error } = await supabase.from('users').select('*').order('id', { ascending: true });
    if (error) { toast.error('Export failed: ' + error.message); return; }
    const formatted = data.map(row => ({
      ...row,
      created_at: row.created_at
        ? new Date(row.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })
        : '',
    }));
    const ws = XLSX.utils.json_to_sheet(formatted);
    // Auto-fit column widths based on content
    const keys = formatted.length ? Object.keys(formatted[0]) : [];
    ws['!cols'] = keys.map(key => {
      const maxLen = formatted.reduce((max, row) => {
        const val = row[key] == null ? '' : String(row[key]);
        return Math.max(max, val.length);
      }, key.length);
      return { wch: Math.min(maxLen + 2, 50) };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members');
    XLSX.writeFile(wb, `members_${getLocalDate()}.xlsx`);
  };
  const [adminEmail, setAdminEmail] = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAdminEmail(session?.user?.email?.toLowerCase() || '');
    });
  }, []);

  useEffect(() => {
    const handler = (e) => { if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayDate = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });

  const filteredUsers = users.filter(u => {
    const nameQ = search.trim().toLowerCase();
    const phoneQ = searchPhone.trim();
    const idQ = searchId.trim();
    const nameMatch = !nameQ || (u.name || '').toLowerCase().includes(nameQ);
    const phoneMatch = !phoneQ || (u.phone || '').includes(phoneQ);
    const idMatch = !idQ || String(u.id).includes(idQ);
    const dateMatch = !dateSearch || (u.created_at && u.created_at.slice(0, 10) === dateSearch);
    return nameMatch && phoneMatch && idMatch && dateMatch;
  });

  const fetchTodayRate = async () => {
    const today = getLocalDate();
    const { data } = await supabase.from('metal_rates').select('*').eq('date', today).maybeSingle();
    setTodayRate(data || null);
    if (!data) setShowRatePopup(true);
  };

  const fetchUsers = async () => {
    setLoading(true);
    const [{ data: userData }, { data: amtData }] = await Promise.all([
      supabase.from('users').select('*').order('created_at', { ascending: false }),
      supabase.from('amounts').select('user_id, amount'),
    ]);

    const amtByUser = {};
    (amtData || []).forEach(a => {
      amtByUser[a.user_id] = (amtByUser[a.user_id] || 0) + Number(a.amount || 0);
    });

    const enriched = (userData || []).map(u => ({
      ...u,
      live_balance: amtByUser[u.id] || 0,
    }));

    setUsers(enriched);
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); fetchTodayRate(); }, []);

  const handleRateSave = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const today = getLocalDate();
    setRateLoading(true);
    const { data, error } = await supabase
      .from('metal_rates')
      .insert([{ date: today, gold_rate: Number(form.get('gold_rate')), silver_rate: Number(form.get('silver_rate')), added_by: adminName }])
      .select()
      .single();
    setRateLoading(false);
    if (!error) {
      setTodayRate(data);
      setShowRatePopup(false);
      toast.success("Today's metal rates saved!");
    } else {
      toast.error(error.message);
    }
  };

  const handleUpdateRate = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const today = getLocalDate();
    setUpdateRateLoading(true);
    const newRate = Number(form.get('rate'));
    const updatedFields = updateRatePopup === 'gold'
      ? { gold_rate: newRate }
      : { silver_rate: newRate };
    let error;
    if (todayRate) {
      // Save history before updating
      await supabase.from('metal_rate_history').insert([{
        metal_rate_id: todayRate.id,
        date: today,
        old_gold_rate: Number(todayRate.gold_rate),
        old_silver_rate: Number(todayRate.silver_rate),
        new_gold_rate: updateRatePopup === 'gold' ? newRate : Number(todayRate.gold_rate),
        new_silver_rate: updateRatePopup === 'silver' ? newRate : Number(todayRate.silver_rate),
        updated_by: adminName,
      }]);
      ({ error } = await supabase.from('metal_rates').update(updatedFields).eq('date', today));
    } else {
      const insertPayload = { date: today, gold_rate: 0, silver_rate: 0, added_by: adminName, ...updatedFields };
      ({ error } = await supabase.from('metal_rates').insert([insertPayload]));
    }
    setUpdateRateLoading(false);
    if (!error) {
      await fetchTodayRate();
      setUpdateRatePopup(null);
      toast.success(`${updateRatePopup === 'gold' ? 'Gold' : 'Silver'} rate updated!`);
    } else {
      toast.error(error.message);
    }
  };

  const guardAction = (fn) => {
    if (!todayRate) {
      toast.error("Please add today's metal rates first", { icon: '⚠️' });
      setShowRatePopup(true);
      return;
    }
    fn();
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success('Logged out');
    navigate('/');
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);

    const payload = Object.fromEntries(new FormData(e.target));

    if (payload.phone && !/^[0-9]{10}$/.test(payload.phone.trim())) {
      toast.error('Phone number must be exactly 10 digits.');
      isSavingRef.current = false;
      setIsSaving(false);
      return;
    }

    if (!selectedUser) {
      // ── Add new member ──
      const { user_id, new_customer_id, created_at: rawCreatedAt, ...rest } = payload;
      const insertPayload = { ...rest, added_by: adminName };
      if (rawCreatedAt) insertPayload.created_at = new Date(rawCreatedAt).toISOString();
      const { data: newUser, error } = await supabase.from('users').insert([insertPayload]).select().single();
      isSavingRef.current = false;
      setIsSaving(false);
      if (!error) {
        // Log to transaction_logs (fire and forget)
        if (newUser) {
          supabase.from('transaction_logs').insert([{
            event_type: 'user_created',
            user_id: Number(newUser.id),
            member_name: newUser.name,
            saving_type: newUser.saving_type,
            phone: newUser.phone,
            performed_by: adminName,
            new_data: { id: String(newUser.id), name: newUser.name, phone: newUser.phone, saving_type: newUser.saving_type, city: newUser.city, created_at: newUser.created_at },
          }]).then(() => {});
        }
        toast.success('New Member Added!');
        setPhoneVal('');
        setIsModalOpen(false);
        fetchUsers();
      } else {
        toast.error(error.message);
      }
      return;
    }

    // ── Edit existing member ──
    const { user_id, new_customer_id, created_at: rawCreatedAt, ...rest } = payload;
    // Convert datetime-local string to ISO timestamptz; drop if unchanged or empty
    if (rawCreatedAt) {
      rest.created_at = new Date(rawCreatedAt).toISOString();
    }

    let error;
    ({ error } = await supabase.from('users').update(rest).eq('id', selectedUser.id));

    isSavingRef.current = false;
    setIsSaving(false);
    if (!error) {
      // Log update to transaction_logs (fire and forget)
      supabase.from('transaction_logs').insert([{
        event_type: 'user_updated',
        user_id: Number(selectedUser.id),
        member_name: rest.name,
        saving_type: rest.saving_type,
        phone: rest.phone,
        performed_by: adminName,
        new_data: { id: String(selectedUser.id), name: rest.name, phone: rest.phone, saving_type: rest.saving_type, city: rest.city },
        old_data: { id: String(selectedUser.id), name: selectedUser.name, phone: selectedUser.phone, saving_type: selectedUser.saving_type, city: selectedUser.city },
      }]).then(() => {});
      toast.success('Member Updated!');
      setPhoneVal('');
      setIsModalOpen(false);
      fetchUsers();
    } else {
      toast.error(error.message);
    }
  };

  const confirmDelete = (id) => {
    const userToDelete = users.find(u => u.id === id);
    setDeleteTarget(userToDelete);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget || isDeletingRef.current) return;
    isDeletingRef.current = true;

    // 1. Fetch all amounts for this user and copy to deleted_amounts + transaction_logs
    const { data: userAmounts } = await supabase
      .from('amounts')
      .select('*')
      .eq('user_id', deleteTarget.id);
    if (userAmounts && userAmounts.length > 0) {
      await supabase.from('deleted_amounts').insert(
        userAmounts.map(a => ({
          original_id: a.id,
          user_id: deleteTarget.id,
          member_name: deleteTarget.name,
          saving_type: deleteTarget.saving_type || 'money',
          amount: Number(a.amount || 0),
          gram: Number(a.gram || 0),
          date: a.date,
          note: a.note || null,
          added_by: a.added_by || null,
          deleted_by: adminName,
        }))
      );
      // Log each amount as amount_deleted in transaction_logs
      supabase.from('transaction_logs').insert(
        userAmounts.map(a => ({
          event_type: 'amount_deleted',
          amount_id: a.id,
          user_id: Number(deleteTarget.id),
          member_name: deleteTarget.name,
          saving_type: deleteTarget.saving_type || 'money',
          amount: Number(a.amount || 0),
          gram: Number(a.gram || 0),
          note: a.note || null,
          performed_by: adminName,
          old_data: {
            amount: a.amount,
            gram: a.gram,
            date: a.date,
            note: a.note,
            added_by: a.added_by,
            deleted_at: new Date().toISOString(),
          },
        }))
      ).then(() => {});
    }

    // 2. Record the user in deleted_users
    await supabase.from('deleted_users').insert([{
      original_id: deleteTarget.id,
      name: deleteTarget.name,
      phone: deleteTarget.phone || null,
      saving_type: deleteTarget.saving_type || 'money',
      total_amount: Number(deleteTarget.total_amount || 0),
      total_gram: Number(deleteTarget.total_gram || 0),
      live_balance: Number(deleteTarget.live_balance || 0),
      joined_at: deleteTarget.created_at || null,
      deleted_by: adminName,
    }]);

    // 3. Delete the user (cascades amounts deletion)
    const { error } = await supabase.from('users').delete().eq('id', deleteTarget.id);
    isDeletingRef.current = false;
    if (!error) {
      // Log deletion to transaction_logs (fire and forget)
      supabase.from('transaction_logs').insert([{
        event_type: 'user_deleted',
        user_id: Number(deleteTarget.id),
        member_name: deleteTarget.name,
        saving_type: deleteTarget.saving_type,
        phone: deleteTarget.phone || null,
        amount: Number(deleteTarget.live_balance || 0),
        gram: Number(deleteTarget.total_gram || 0),
        performed_by: adminName,
        old_data: {
          original_id: String(deleteTarget.id),
          name: deleteTarget.name,
          phone: deleteTarget.phone,
          saving_type: deleteTarget.saving_type,
          live_balance: deleteTarget.live_balance,
          total_gram: deleteTarget.total_gram,
          deleted_at: new Date().toISOString(),
        },
      }]).then(() => {});
      setDeleteTarget(null);
      fetchUsers();
      toast.success('Member Deleted Successfully');
    } else {
      setDeleteTarget(null);
      toast.error(error.message);
    }
  };

  if (loading) return <LogoLoader />;

  return (
    <div style={s.page}>

      {/* Rate Entry Popup — auto-opens when no rate for today */}
      {showRatePopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 3000,
          background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '20px', padding: '32px 28px',
            width: '100%', maxWidth: '420px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <TrendingUp size={28} color="#fff" />
              </div>
              <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Set Today's Metal Rates</h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{displayDate} — Rates not set for today</p>
            </div>
            <form onSubmit={handleRateSave}>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gold Rate (₹/g)</label>
              <input
                name="gold_rate" type="number" step="0.01" min="0"
                placeholder="e.g. 6500.00" required
                style={{ ...s.inputStyle, marginTop: '6px', borderColor: '#C9A84C' }}
              />
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Silver Rate (₹/g)</label>
              <input
                name="silver_rate" type="number" step="0.01" min="0"
                placeholder="e.g. 75.00" required
                style={{ ...s.inputStyle, marginTop: '6px', borderColor: '#94a3b8' }}
              />
              <button type="submit" disabled={rateLoading} style={{ ...s.saveBtn, marginTop: '8px', opacity: rateLoading ? 0.7 : 1 }}>
                {rateLoading ? 'Saving…' : "Save Today's Rates"}
              </button>
            </form>
            <button
              onClick={handleLogout}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%', marginTop: '12px', padding: '10px', borderRadius: '9px', border: '1.5px solid #fca5a5', background: 'transparent', color: '#ef4444', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      )}
      {/* Navbar */}
      <nav style={s.navbar} className="gs-navbar">
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} className="gs-nav-coin" onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
          </div>
          <div>
            <p style={s.navTitle} className="gs-nav-title">Al Ameen Gold & Silver</p>
            <p style={s.navSub} className="gs-nav-sub">Gold & Money Saving</p>
          </div>
        </div>
        <div style={s.navRight}>
          {adminEmail === ALLOWED_ADMIN_EMAIL && (
            <button onClick={() => navigate('/admin')} style={s.btnAdmin} className="gs-nav-btn">
              <Shield size={15} /><span className="gs-btn-label">&nbsp;Admin</span>
            </button>
          )}
          {/* Avatar dropdown */}
          <div ref={avatarRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setAvatarOpen(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer' }}
            >
              <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'linear-gradient(135deg,#C9A84C,#9A7A2C)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', color: '#fff', flexShrink: 0 }}>
                {adminName ? adminName.charAt(0).toUpperCase() : '?'}
              </div>
              <ChevronDown size={13} color="#cbd5e1" />
            </button>
            {avatarOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 300, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #f1f5f9', minWidth: '220px', padding: '12px', }}>                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', background: '#f8fafc', marginBottom: '8px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg,#C9A84C,#9A7A2C)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '800', color: '#fff', flexShrink: 0 }}>
                    {adminName ? adminName.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div style={{ overflow: 'hidden' }}>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{adminName || 'Admin'}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setAvatarOpen(false); setLogoutConfirm(true); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 10px', borderRadius: '8px', border: 'none', background: 'transparent', color: '#ef4444', fontSize: '13px', fontWeight: '700', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <LogOut size={14} /> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={s.content} className="gs-content">
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }} className="gs-rate-cards">
          {/* Gold Rate — Hero Card */}
          <div className="gs-shimmer-gold gs-rate-card" onDoubleClick={() => setUpdateRatePopup('gold')} title="Double-click to update gold rate" style={{ borderRadius: '16px', padding: '22px 28px', background: 'linear-gradient(135deg, #78350f, #92400e, #B45309)', boxShadow: '0 8px 28px rgba(180,83,9,0.4)', position: 'relative', overflow: 'hidden', cursor: 'pointer' }}>
            {/* Animated orbs */}
            <div className="gs-orb" style={{ width: '130px', height: '130px', background: 'radial-gradient(circle, rgba(251,191,36,0.28) 0%, transparent 70%)', right: '-25px', top: '-25px', animationDuration: '4s', animationDelay: '0s' }} />
            <div className="gs-orb" style={{ width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(251,191,36,0.22) 0%, transparent 70%)', left: '5px', bottom: '-15px', animationDuration: '5s', animationDelay: '1.5s' }} />
            <div className="gs-orb" style={{ width: '50px', height: '50px', background: 'radial-gradient(circle, rgba(251,191,36,0.18) 0%, transparent 70%)', left: '45%', top: '10px', animationDuration: '6s', animationDelay: '2s' }} />
            {/* Particles */}
            <div className="gs-particle" style={{ width: '5px', height: '5px', background: 'rgba(251,191,36,0.7)', left: '18%', bottom: '14px', animationDuration: '2.8s', animationDelay: '0s' }} />
            <div className="gs-particle" style={{ width: '4px', height: '4px', background: 'rgba(251,191,36,0.5)', left: '35%', bottom: '10px', animationDuration: '3.4s', animationDelay: '1s' }} />
            <div className="gs-particle" style={{ width: '3px', height: '3px', background: 'rgba(251,191,36,0.6)', left: '55%', bottom: '18px', animationDuration: '2.6s', animationDelay: '0.5s' }} />
            {/* Pulse ring */}
            <div className="gs-pulse-ring" style={{ width: '64px', height: '64px', border: '2px solid rgba(251,191,36,0.45)', right: '22px', top: '16px', animationDuration: '2s', animationDelay: '0s' }} />
            <div className="gs-pulse-ring" style={{ width: '64px', height: '64px', border: '2px solid rgba(251,191,36,0.25)', right: '22px', top: '16px', animationDuration: '2s', animationDelay: '0.7s' }} />
            {/* Spinning arc */}
            <div className="gs-spin-arc" style={{ width: '54px', height: '54px', borderTopColor: 'rgba(251,191,36,0.6)', borderRightColor: 'rgba(251,191,36,0.25)', right: '27px', top: '21px', animationDuration: '3s' }} />
            {/* LIVE text inside circle */}
            <div style={{ position: 'absolute', right: '22px', top: '16px', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'none' }}>
              <span style={{ fontSize: '10px', fontWeight: '900', color: '#FBBF24', letterSpacing: '1px', textShadow: '0 0 8px rgba(251,191,36,0.8)' }}>LIVE</span>
            </div>
            {/* Content */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span className="gs-rate-icon">🥇</span>
                <span className="gs-rate-label" style={{ fontWeight: '700', color: '#fde68a', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Gold Rate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <div className="gs-rate-value gs-gold-val" style={{ fontWeight: '900', color: '#FBBF24', letterSpacing: '-1px', lineHeight: 1.1 }}>
                  {todayRate ? `₹${Number(todayRate.gold_rate).toLocaleString('en-IN')}` : <span style={{ fontSize: '16px', color: '#fca5a5' }}>Not set today</span>}
                </div>
                {todayRate && <span className="gs-rate-unit" style={{ color: '#fde68a', fontWeight: '600' }}>/g</span>}
              </div>
              {todayRate && <div className="gs-rate-date" style={{ color: '#fde68a', fontWeight: '600', marginTop: '4px' }}>{displayDate}</div>}
            </div>
          </div>
          {/* Silver Rate — Hero Card */}
          <div className="gs-shimmer-silver gs-rate-card" onDoubleClick={() => setUpdateRatePopup('silver')} title="Double-click to update silver rate" style={{ borderRadius: '16px', padding: '22px 28px', background: 'linear-gradient(135deg, #1e293b, #334155, #475569)', boxShadow: '0 8px 28px rgba(71,85,105,0.4)', position: 'relative', overflow: 'hidden', cursor: 'pointer' }}>
            {/* Animated orbs */}
            <div className="gs-orb" style={{ width: '130px', height: '130px', background: 'radial-gradient(circle, rgba(226,232,240,0.22) 0%, transparent 70%)', right: '-25px', top: '-25px', animationDuration: '4.5s', animationDelay: '0s' }} />
            <div className="gs-orb" style={{ width: '80px', height: '80px', background: 'radial-gradient(circle, rgba(226,232,240,0.16) 0%, transparent 70%)', left: '5px', bottom: '-15px', animationDuration: '5.5s', animationDelay: '2s' }} />
            <div className="gs-orb" style={{ width: '50px', height: '50px', background: 'radial-gradient(circle, rgba(226,232,240,0.14) 0%, transparent 70%)', left: '45%', top: '10px', animationDuration: '6.5s', animationDelay: '1s' }} />
            {/* Particles */}
            <div className="gs-particle" style={{ width: '5px', height: '5px', background: 'rgba(226,232,240,0.65)', left: '18%', bottom: '14px', animationDuration: '3s', animationDelay: '0s' }} />
            <div className="gs-particle" style={{ width: '4px', height: '4px', background: 'rgba(226,232,240,0.45)', left: '35%', bottom: '10px', animationDuration: '3.6s', animationDelay: '1.2s' }} />
            <div className="gs-particle" style={{ width: '3px', height: '3px', background: 'rgba(226,232,240,0.55)', left: '55%', bottom: '18px', animationDuration: '2.8s', animationDelay: '0.7s' }} />
            {/* Pulse ring */}
            <div className="gs-pulse-ring" style={{ width: '64px', height: '64px', border: '2px solid rgba(226,232,240,0.38)', right: '22px', top: '16px', animationDuration: '2.2s', animationDelay: '0s' }} />
            <div className="gs-pulse-ring" style={{ width: '64px', height: '64px', border: '2px solid rgba(226,232,240,0.22)', right: '22px', top: '16px', animationDuration: '2.2s', animationDelay: '0.8s' }} />
            {/* Spinning arc */}
            <div className="gs-spin-arc" style={{ width: '54px', height: '54px', borderTopColor: 'rgba(226,232,240,0.52)', borderRightColor: 'rgba(226,232,240,0.2)', right: '27px', top: '21px', animationDuration: '3.5s' }} />
            {/* LIVE text inside circle */}
            <div style={{ position: 'absolute', right: '22px', top: '16px', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'none' }}>
              <span style={{ fontSize: '10px', fontWeight: '900', color: '#e2e8f0', letterSpacing: '1px', textShadow: '0 0 8px rgba(226,232,240,0.7)' }}>LIVE</span>
            </div>
            {/* Content */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span className="gs-rate-icon">🥈</span>
                <span className="gs-rate-label" style={{ fontWeight: '700', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Silver Rate</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                <div className="gs-rate-value gs-silver-val" style={{ fontWeight: '900', color: '#e2e8f0', letterSpacing: '-1px', lineHeight: 1.1 }}>
                  {todayRate ? `₹${Number(todayRate.silver_rate).toLocaleString('en-IN')}` : <span style={{ fontSize: '16px', color: '#fca5a5' }}>Not set today</span>}
                </div>
                {todayRate && <span className="gs-rate-unit" style={{ color: '#94a3b8', fontWeight: '600' }}>/g</span>}
              </div>
              {todayRate && <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', marginTop: '4px' }}>{displayDate}</div>}
            </div>
          </div>
        </div>
        {/* Small stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }} className="gs-stats-4col">
          <div style={{ ...s.statCard, padding: '14px 18px' }} className="gs-stat-card-0">
            <div style={s.statLabel}>Total Members</div>
            <div style={{ ...s.statValue, fontSize: '22px' }} className="gs-stat-val">{users.length}</div>
          </div>
          <div style={{ ...s.statCard, padding: '14px 18px', borderLeftColor: '#D97706' }} className="gs-stat-card-1">
            <div style={s.statLabel}>Gold Members</div>
            <div style={{ ...s.statValue, color: '#D97706', fontSize: '22px' }} className="gs-stat-val">{users.filter(u => u.saving_type === 'gold').length}</div>
          </div>
          <div style={{ ...s.statCard, padding: '14px 18px', borderLeftColor: '#64748b' }} className="gs-stat-card-2">
            <div style={s.statLabel}>Silver Members</div>
            <div style={{ ...s.statValue, color: '#475569', fontSize: '22px' }} className="gs-stat-val">{users.filter(u => u.saving_type === 'silver').length}</div>
          </div>
          <div style={{ ...s.statCard, padding: '14px 18px', borderLeftColor: '#16a34a' }} className="gs-stat-card-3">
            <div style={s.statLabel}>Money Members</div>
            <div style={{ ...s.statValue, color: '#16a34a', fontSize: '22px' }} className="gs-stat-val">{users.filter(u => u.saving_type === 'money').length}</div>
          </div>
        </div>

        {/* Table */}
        <div style={s.tableCard} className="gs-members-container">
          <div style={s.tableHeader} className="gs-tbl-hdr">
            <h3 style={s.tableTitle}>Members</h3>
            <div className="gs-tbl-actions">
              {/* Search row */}
              <div className="gs-tbl-search-row">
                {/* Search: ID */}
                <div className="gs-si gs-si-sm">
                  <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '12px', fontWeight: '700' }}>#</div>
                  <input
                    value={searchId}
                    onChange={e => setSearchId(e.target.value)}
                    placeholder="ID..."
                    style={{ padding: '8px 30px 8px 26px', border: '1.5px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', color: '#334155', background: '#f8fafc', outline: 'none', width: '90px', boxSizing: 'border-box' }}
                  />
                  {searchId && <button onClick={() => setSearchId('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 0 }}><X size={13} /></button>}
                </div>
                {/* Search: Phone */}
                <div className="gs-si">
                  <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none', fontSize: '12px' }}>📞</div>
                  <input
                    value={searchPhone}
                    onChange={e => setSearchPhone(e.target.value)}
                    placeholder="Phone..."
                    style={{ padding: '8px 30px 8px 30px', border: '1.5px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', color: '#334155', background: '#f8fafc', outline: 'none', width: '130px', boxSizing: 'border-box' }}
                  />
                  {searchPhone && <button onClick={() => setSearchPhone('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 0 }}><X size={13} /></button>}
                </div>
                {/* Search: Name */}
                <div className="gs-si">
                  <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}><Search size={14} /></div>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Name..."
                    style={{ padding: '8px 30px 8px 30px', border: '1.5px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', color: '#334155', background: '#f8fafc', outline: 'none', width: '130px', boxSizing: 'border-box' }}
                  />
                  {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 0 }}><X size={13} /></button>}
                </div>
                {/* Date filter */}
                <div className="gs-date-grp">
                  <input
                    type="date"
                    value={dateSearch}
                    onChange={e => setDateSearch(e.target.value)}
                    style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', color: dateSearch ? '#334155' : '#94a3b8', background: '#f8fafc', outline: 'none', cursor: 'pointer' }}
                  />
                  {dateSearch && (
                    <button onClick={() => setDateSearch('')} style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px', borderRadius: '7px', flexShrink: 0 }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>
              {/* Action buttons */}
              <div className="gs-tbl-btns">
                <button
                  onClick={() => guardAction(() => { setSelectedUser(null); setPhoneVal(''); setIsModalOpen(true); })}
                  style={s.btnAdd}
                >
                  <Plus size={16} /> Add Member
                </button>
                {adminEmail === ALLOWED_ADMIN_EMAIL && (
                  <button
                    onClick={handleExportXLS}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #16a34a, #15803d)', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <Download size={15} /> Export XLS
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="gs-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '520px' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Customer ID</th>
                  <th style={s.th}>Name</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Phone</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>City</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Type</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Total Gram</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Balance</th>
                  <th style={s.th}>Added By</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Joined</th>
                  <th style={{ ...s.th, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={10} style={{ ...s.td, textAlign: 'center', color: '#94a3b8', padding: '40px' }}>{(search || searchPhone || searchId) ? 'No results found.' : 'No members yet. Add one!'}</td></tr>
                )}
                {filteredUsers.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={s.td}><span style={s.badge}>{u.id}</span></td>
                    <td style={{ ...s.td, fontWeight: '600', color: '#2563eb', cursor: 'pointer' }} onClick={() => navigate(`/user/${u.id}`)}>{u.name}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{u.phone || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{u.city || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      {u.saving_type === 'gold' ? (
                        <span style={{ display: 'inline-block', background: '#fef3c7', color: '#D97706', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>Gold</span>
                      ) : u.saving_type === 'silver' ? (
                        <span style={{ display: 'inline-block', background: '#f1f5f9', color: '#475569', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>Silver</span>
                      ) : (
                        <span style={{ display: 'inline-block', background: '#dcfce7', color: '#16a34a', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>Money</span>
                      )}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      {u.saving_type !== 'money' && Number(u.total_gram || 0) > 0 ? (
                        <span style={{ display: 'inline-block', background: u.saving_type === 'gold' ? '#fef3c7' : '#f1f5f9', color: u.saving_type === 'gold' ? '#D97706' : '#475569', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>
                          {Number(u.total_gram).toFixed(3)}g
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...s.td, fontWeight: '700', color: '#D97706', textAlign: 'center' }}>₹{Number(u.live_balance || 0).toLocaleString('en-IN')}</td>
                    <td style={{ ...s.td, maxWidth: '130px' }}>
                      <span
                        title={u.added_by || '—'}
                        style={{
                          fontSize: '13px', color: '#64748b',
                          display: 'block', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          maxWidth: '130px', cursor: 'default',
                        }}
                      >{u.added_by || '—'}</span>
                    </td>
                    <td style={{ ...s.td, textAlign: 'center', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <div style={s.actions}>
                        <button title="Edit" style={{ ...s.iconBtn, color: '#10b981' }} onClick={() => guardAction(() => { setSelectedUser(u); setPhoneVal(u.phone || ''); setIsModalOpen(true); })}><Edit size={17} /></button>
                        {adminEmail === ALLOWED_ADMIN_EMAIL && (
                          <button title="Delete" style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => guardAction(() => confirmDelete(u.id))}><Trash2 size={17} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
            <span>{filteredUsers.length} {filteredUsers.length === 1 ? 'Record shown' : 'Records shown'}</span>
            {(search || searchPhone || searchId || dateSearch) && <span style={{ color: '#C9A84C', fontWeight: '700' }}>⚡ Filters applied</span>}
          </div>
        </div>
      </div>
      </div>

      {/* Delete Confirmation Modal */}
      {/* Update Rate Popup */}
      {updateRatePopup && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '18px', padding: '28px 28px 24px', width: '100%', maxWidth: '360px', boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: updateRatePopup === 'gold' ? 'linear-gradient(135deg,#92400e,#B45309)' : 'linear-gradient(135deg,#334155,#475569)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '22px' }}>
                {updateRatePopup === 'gold' ? '🥇' : '🥈'}
              </div>
              <h3 style={{ margin: '0 0 4px', fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                Update {updateRatePopup === 'gold' ? 'Gold' : 'Silver'} Rate
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                Today · {displayDate}
                {todayRate && (
                  <span style={{ display: 'block', marginTop: '4px', fontWeight: '700', color: updateRatePopup === 'gold' ? '#D97706' : '#475569' }}>
                    Current: ₹{Number(updateRatePopup === 'gold' ? todayRate.gold_rate : todayRate.silver_rate).toLocaleString('en-IN')}/g
                  </span>
                )}
              </p>
            </div>
            <form onSubmit={handleUpdateRate}>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                New {updateRatePopup === 'gold' ? 'Gold' : 'Silver'} Rate (₹/g)
              </label>
              <input
                name="rate"
                type="number"
                step="0.01"
                min="0"
                defaultValue={todayRate ? (updateRatePopup === 'gold' ? todayRate.gold_rate : todayRate.silver_rate) : ''}
                placeholder="e.g. 9500.00"
                required
                autoFocus
                style={{ width: '100%', padding: '11px 14px', border: `1.5px solid ${updateRatePopup === 'gold' ? '#C9A84C' : '#94a3b8'}`, borderRadius: '9px', fontSize: '15px', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', marginBottom: '16px' }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setUpdateRatePopup(null)} style={{ flex: 1, padding: '11px', borderRadius: '9px', border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: '14px', fontWeight: '700', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={updateRateLoading} style={{ flex: 1, padding: '11px', borderRadius: '9px', border: 'none', background: updateRatePopup === 'gold' ? 'linear-gradient(135deg,#92400e,#B45309)' : 'linear-gradient(135deg,#334155,#475569)', fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer', opacity: updateRateLoading ? 0.7 : 1 }}>
                  {updateRateLoading ? 'Saving…' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {logoutConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div className="gs-modal-box" style={{
            background: '#fff', borderRadius: '16px', padding: '28px 28px 24px',
            width: '100%', maxWidth: '380px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: 'rgba(201,168,76,0.12)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <LogOut size={26} color="#C9A84C" />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '19px', fontWeight: '800', color: '#0f172a' }}>Logout?</h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>Are you sure you want to logout?</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={() => setLogoutConfirm(false)}
                style={{
                  flex: 1, padding: '11px', borderRadius: '9px',
                  border: '1.5px solid #e2e8f0', background: '#f8fafc',
                  fontSize: '14px', fontWeight: '700', color: '#64748b', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1, padding: '11px', borderRadius: '9px',
                  border: 'none', background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)',
                  fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(201,168,76,0.35)',
                }}
              >Yes, Logout</button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }}>
          <div className="gs-modal-box" style={{
            background: '#fff', borderRadius: '16px', padding: '28px 28px 24px',
            width: '100%', maxWidth: '400px',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '8px' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: 'rgba(239,68,68,0.1)', display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <Trash2 size={26} color="#ef4444" />
              </div>
              <h2 style={{ margin: '0 0 8px', fontSize: '19px', fontWeight: '800', color: '#0f172a' }}>Delete Member?</h2>
              <p style={{ margin: '0 0 6px', fontSize: '14px', color: '#64748b' }}>You are about to permanently delete:</p>
              <p style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{deleteTarget.name}</p>
              <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>#{deleteTarget.id} · {deleteTarget.phone || 'No phone'}</p>
              <div style={{
                margin: '14px 0 0', background: '#fef2f2', borderRadius: '8px',
                padding: '10px 14px',
              }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>
                  Balance: ₹{Number(deleteTarget.live_balance || 0).toLocaleString('en-IN')}
                </p>
                {deleteTarget.saving_type !== 'money' && Number(deleteTarget.total_gram || 0) > 0 && (
                  <p style={{ margin: 0, fontSize: '13px', color: deleteTarget.saving_type === 'gold' ? '#D97706' : '#475569', fontWeight: '600' }}>
                    {deleteTarget.saving_type === 'gold' ? '🥇' : '🥈'} {Number(deleteTarget.total_gram).toFixed(3)} g {deleteTarget.saving_type === 'gold' ? 'Gold' : 'Silver'}
                  </p>
                )}
              </div>
              <p style={{ margin: '12px 0 0', fontSize: '12px', color: '#94a3b8' }}>This action cannot be undone.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{
                  flex: 1, padding: '11px', borderRadius: '9px',
                  border: '1.5px solid #e2e8f0', background: '#f8fafc',
                  fontSize: '14px', fontWeight: '700', color: '#64748b', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={handleDeleteConfirmed}
                style={{
                  flex: 1, padding: '11px', borderRadius: '9px',
                  border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                  fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(239,68,68,0.35)',
                }}
              >Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setPhoneVal(''); }} title={selectedUser ? 'Edit Member' : 'Add New Member'}>
        <form onSubmit={handleSaveUser}>
          {selectedUser && (
            <div style={{ ...s.inputStyle, background: '#f1f5f9', color: '#64748b', cursor: 'default', display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
              Customer ID:&nbsp;<strong style={{ color: '#0f172a' }}>{selectedUser.id}</strong>
            </div>
          )}
          <input name="name" defaultValue={selectedUser?.name} placeholder="Full Name *" required style={s.inputStyle} />
          <input name="phone" placeholder="Phone Number * (10 digits)" required maxLength={10} pattern="[0-9]{10}" inputMode="numeric" value={phoneVal} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setPhoneVal(v); }} onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }} style={{ ...s.inputStyle}} />
          {phoneVal.length > 0 && phoneVal.length < 10 && (
            <p style={{ margin: '-8px 0 6px', fontSize: '12px', color: '#ef4444', fontWeight: '600' }}>Please enter 10 digits</p>
          )}
          <input name="city" defaultValue={selectedUser?.city} placeholder="City *" required style={s.inputStyle} />
          <input name="pincode" defaultValue={selectedUser?.pincode} placeholder="Pincode" style={s.inputStyle} />
          <input name="father_name" defaultValue={selectedUser?.father_name} placeholder="Father's Name" style={s.inputStyle} />
          <input name="adhar_num" defaultValue={selectedUser?.adhar_num} placeholder="Aadhar Number" style={s.inputStyle} />
          <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Saving Type</label>
          {selectedUser ? (
            <>
              <input
                name="saving_type"
                value={selectedUser.saving_type === 'gold' ? '🥇 Gold Saving' : selectedUser.saving_type === 'silver' ? '🥈 Silver Saving' : '💰 Money Saving'}
                readOnly
                style={{ ...s.inputStyle, background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }}
              />
              <input type="hidden" name="saving_type" value={selectedUser.saving_type} />
            </>
          ) : (
            <select name="saving_type" defaultValue="gold" required style={{ ...s.inputStyle, cursor: 'pointer' }}>
              <option value="money">💰 Money Saving</option>
              <option value="gold">🥇 Gold Saving</option>
              <option value="silver">🥈 Silver Saving</option>
            </select>
          )}
          {selectedUser && adminEmail === ALLOWED_ADMIN_EMAIL && (
            <>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Joined Date &amp; Time</label>
              <input
                name="created_at"
                type="datetime-local"
                defaultValue={selectedUser.created_at ? new Date(new Date(selectedUser.created_at).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                style={{ ...s.inputStyle, cursor: 'pointer' }}
              />
            </>
          )}
          {!selectedUser && adminEmail === ALLOWED_ADMIN_EMAIL && (
            <>
              <label style={{ fontSize: '12px', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '5px' }}>Joined Date &amp; Time</label>
              <input
                name="created_at"
                type="datetime-local"
                defaultValue={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                style={{ ...s.inputStyle, cursor: 'pointer' }}
              />
            </>
          )}
          <button type="submit" disabled={isSaving} style={{ ...s.saveBtn, opacity: isSaving ? 0.7 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}>
            {isSaving ? 'Saving…' : selectedUser ? 'Update Details' : 'Save Details'}
          </button>
        </form>
      </Modal>
    </div>
  );
}